import PropTypes from "prop-types";
import classNames from "classnames";
import {
  Button,
  ButtonGroup,
  CheckPicker,
  Col,
  Input,
  InputGroup,
  Row,
} from "rsuite";
import FunnelIcon from "@rsuite/icons/Funnel";
import SearchIcon from "@rsuite/icons/Search";
import DownloadIcon from "../../assets/img/download-icon.svg?react";
import SortArrowIcon from "../../assets/img/sort-arrow.svg?react";
import Table from "../../components/common/table/Table";
import {
  ACTION_TYPE_OPTIONS,
  AREA_BADGES,
  COLUMNS,
  ChipIcon,
  GRID_HELPER_TEXT,
  actionDescription,
  actionLabel,
  displayHotelCode,
  displayHotelName,
  formatActivityDate,
  formatActivityTimeEst,
  formatActivityTimeLocal,
  hotelTimeZone,
} from "./utils";

const { Column, HeaderCell, Cell } = Table;

// Replaces rsuite's built-in sort glyph with the Figma arrow. rsuite only calls
// this for sortable columns, passing the active sortType for the column that is
// currently sorted and undefined for the rest.
const renderSortIcon = (sortType) => (
  <SortArrowIcon
    className={classNames("user-activity-sort-icon", {
      "user-activity-sort-icon--active": Boolean(sortType),
    })}
  />
);

const AreaBadge = ({ area }) => {
  if (!area) return null;
  const cfg = AREA_BADGES[area];
  if (cfg) {
    return (
      <span className={`activity-type-badge ${cfg.className}`}>
        {cfg.label}
      </span>
    );
  }
  // Unknown area — render a neutral badge so the row is still labelled.
  return <span className="activity-type-badge badge-default">{area}</span>;
};

AreaBadge.propTypes = { area: PropTypes.string };

const UserActivityResults = ({
  data,
  loading,
  searchSummary,
  totalEstimate,
  totalCapped,
  hasNext,
  hasPrev,
  onNext,
  onPrev,
  onEditSearch,
  onRowClick,
  sortColumn,
  sortType,
  onSortColumn,
  areaFilter,
  onAreaFilterChange,
  searchText,
  onSearchTextChange,
  onDownload,
  downloading,
}) => {
  // Sorting, the action-type filter and the search box are all applied server-side
  // across the whole result set, so the table renders the API rows verbatim.
  const rows = data;

  // rsuite passes no sortType on the first click of a not-yet-sorted column;
  // default it to ascending so the server always gets a concrete sort_order.
  const handleSort = (dataKey, type) => onSortColumn(dataKey, type || "asc");

  const renderCell = (rowData, dataKey) => {
    switch (dataKey) {
      case "hotel_name": {
        const value = displayHotelName(rowData);
        return <div title={value}>{value}</div>;
      }
      case "hotel_code": {
        const value = displayHotelCode(rowData);
        return <div title={value}>{value}</div>;
      }
      case "date": {
        const value = formatActivityDate(
          rowData.occurred_at,
          hotelTimeZone(rowData)
        );
        return <div title={value}>{value}</div>;
      }
      case "time_local": {
        const tz = hotelTimeZone(rowData);
        const value = formatActivityTimeLocal(rowData.occurred_at, tz);
        // The zone is worth surfacing: two rows in one table can be on different clocks.
        return <div title={tz || undefined}>{value}</div>;
      }
      case "time_est": {
        const value = formatActivityTimeEst(rowData.occurred_at);
        return <div>{value}</div>;
      }
      case "type": {
        // Pill + action description, as in the Activity Details Type row. Not the
        // summary sentence: it leads with the user's email.
        const label =
          actionDescription(rowData.action) || actionLabel(rowData.action);
        return (
          <div className="user-activity-type-cell">
            <AreaBadge area={rowData.area} />
            <span className="user-activity-type-cell__text">{label}</span>
            {rowData.outcome && rowData.outcome !== "SUCCESS" && (
              <span className="activity-outcome-badge" title={rowData.outcome}>
                {rowData.outcome}
              </span>
            )}
          </div>
        );
      }
      default: {
        const value = rowData[dataKey];
        const text =
          value === undefined || value === null || value === "" ? "—" : value;
        return <div title={text}>{text}</div>;
      }
    }
  };

  const resultNoun = totalEstimate === 1 ? "result" : "results";
  const totalText = totalCapped
    ? "10,000+ results"
    : `${totalEstimate} ${resultNoun}`;

  return (
    <>
      <Row className="user-activity-summary">
        {searchSummary.map((chip) => (
          <Col key={chip.key} className="user-activity-summary__chip">
            <ChipIcon chipKey={chip.key} />
            <div className="user-activity-summary__text">
              <span className="user-activity-summary__label">{chip.label}</span>
              <span className="user-activity-summary__value">
                {chip.value}
                {chip.extraCount > 0 && (
                  <span className="user-activity-summary__more">
                    +{chip.extraCount}
                  </span>
                )}
              </span>
            </div>
          </Col>
        ))}
        <Col className="user-activity-summary__edit">
          <Button appearance="ghost" onClick={onEditSearch}>
            Edit search
          </Button>
        </Col>
      </Row>

      <p className="user-activity-results-hint">{GRID_HELPER_TEXT}</p>

      <div className="user-activity-results-card">
        <div className="user-activity-results-card__toolbar">
          <div className="user-activity-results-card__filter">
            <FunnelIcon />
            <CheckPicker
              className="user-activity-results-card__type-select"
              data={ACTION_TYPE_OPTIONS}
              value={areaFilter}
              onChange={(val) => onAreaFilterChange(val || [])}
              placeholder="All action types"
              searchable={false}
              container={() => document.body}
            />
          </div>
          <div className="user-activity-results-card__actions">
            <InputGroup inside className="user-activity-results-card__search">
              <InputGroup.Addon>
                <SearchIcon />
              </InputGroup.Addon>
              <Input
                placeholder="Search"
                value={searchText}
                onChange={onSearchTextChange}
              />
            </InputGroup>
            <Button
              appearance="ghost"
              className="user-activity-results-card__download"
              onClick={onDownload}
              loading={downloading}
              // Nothing on screen means nothing to export; the server would return an
              // empty sheet.
              disabled={loading || downloading || rows.length === 0}
            >
              <DownloadIcon aria-hidden="true" />
              <span>Download</span>
            </Button>
          </div>
        </div>

        <Table
          data={rows}
          loading={loading}
          onRowClick={onRowClick}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={handleSort}
        >
          {COLUMNS.map((column) => (
            <Column
              key={column.dataKey}
              width={column.width}
              minWidth={column.minWidth}
              flexGrow={column.flexGrow}
              sortable={column.sortable}
            >
              <HeaderCell renderSortIcon={renderSortIcon}>
                <div className="header-cell" title={column.label}>
                  {column.label}
                </div>
              </HeaderCell>
              <Cell dataKey={column.dataKey}>
                {(rowData) => renderCell(rowData, column.dataKey)}
              </Cell>
            </Column>
          ))}
        </Table>

        <div className="user-activity-results-card__footer">
          <span className="user-activity-results-card__count">{totalText}</span>
          <ButtonGroup>
            <Button onClick={onPrev} disabled={!hasPrev || loading}>
              Previous
            </Button>
            <Button onClick={onNext} disabled={!hasNext || loading}>
              Next
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </>
  );
};

UserActivityResults.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  searchSummary: PropTypes.array,
  totalEstimate: PropTypes.number,
  totalCapped: PropTypes.bool,
  hasNext: PropTypes.bool,
  hasPrev: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onEditSearch: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  sortColumn: PropTypes.string,
  sortType: PropTypes.oneOf(["asc", "desc"]),
  onSortColumn: PropTypes.func.isRequired,
  areaFilter: PropTypes.arrayOf(PropTypes.string),
  onAreaFilterChange: PropTypes.func.isRequired,
  searchText: PropTypes.string,
  onSearchTextChange: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  downloading: PropTypes.bool,
};

UserActivityResults.defaultProps = {
  data: [],
  loading: false,
  searchSummary: [],
  totalEstimate: 0,
  totalCapped: false,
  hasNext: false,
  hasPrev: false,
  sortColumn: null,
  sortType: null,
  downloading: false,
};

export default UserActivityResults;
